import { useHttp } from "../http/useHttp";
import { useAxiosQuery } from "../http/useAxiosQuery";
import { dayjs, useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type DashboardContractsOperation = paths["/dashboard/contracts"]["post"];
type UseContractsResponse =
  DashboardContractsOperation["responses"][200]["content"]["application/json"];

export type IContractResponse = UseContractsResponse[number];
export type DashboardContractState = IContractResponse["contract_state"];
export type IDashboardContract = IContractResponse & {
  contract_display_name: string;
  last_invoice: string | null;
};
export type UseContractsInput =
  DashboardContractsOperation["requestBody"]["content"]["application/json"];

function getContractLastInvoice(contract: IContractResponse) {
  if (contract.generated_last_invoice) {
    return null;
  }

  if (!contract.last_invoice_date) {
    if (!contract.begin_time) {
      return null;
    }

    const startOfContractInvoice = dayjs(contract.begin_time).add(
      contract.invoice_delay_in_seconds || 0,
      "seconds",
    );
    if (startOfContractInvoice.isAfter(dayjs())) {
      return null;
    }

    return startOfContractInvoice.toISOString();
  }

  return contract.last_invoice_date;
}

export function transformContract(contract: IContractResponse): IDashboardContract {
  return {
    ...contract,
    contract_display_name:
      contract.contract_names.find(({ name }) => name)?.name ||
      contract.consumer_contract_name ||
      contract.supplier_contract_name ||
      "",
    last_invoice: getContractLastInvoice(contract),
  };
}

export const useContracts = (input: UseContractsInput = {}) => {
  const { client } = useHttp();
  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseContractsResponse>({
      queryKey: ["contracts", input],
      queryFn: ({ signal }) => client.post("/contracts", input, { signal }),
      gcTime: Infinity,
    });

  useDefaultError(error, "Failed to get contracts");

  return {
    contracts: data
      ? [...data.data]
          .map(transformContract)
          .sort((a, b) => (b.contract_state || "").localeCompare(a.contract_state || ""))
      : null,
    status,
    isPending,
    isFetching,
    isLoading,
    error,
    refetch,
  };
};

export type UseContractsReturn = ReturnType<typeof useContracts>;
